/**
 * Sync compression helpers — shared by both server (userdo.ts) and client
 * (use-user-do-sync.ts).
 *
 * Used to gzip large WebSocket messages (`full-seed`, `sync-diff`) that can
 * exceed practical string-frame sizes for users with 50MB+ data.
 *
 * Two transport shapes coexist:
 *   - Plain text: the message is JSON — `JSON.parse(payload)` directly.
 *   - Compressed: payload is `gz:<base64-gzip>`. Sender emits via
 *     `encodeCompressedString`, receiver decodes via `decodeCustomPayload`.
 *
 * String-based envelope is required because y-partyserver's custom-message
 * channel (`__YPS:` prefix) is string-only — binary frames are reserved for
 * the Yjs sync protocol.
 */

import { gzip, Inflate } from 'pako';

/**
 * Compress a message object to a gzipped Uint8Array for binary WebSocket send.
 */
export function compressMessage(msg: unknown): Uint8Array {
  const json = JSON.stringify(msg);
  return gzip(json);
}

/**
 * Compress a pre-serialized JSON string. Avoids double-serialization when
 * the caller needs both the JSON string (for logging) and the compressed bytes.
 */
export function compressJsonString(json: string): Uint8Array {
  return gzip(json);
}

/**
 * Decompress a binary WebSocket message (gzipped) back to a parsed object.
 *
 * Guards against decompression bombs via streaming inflation: the
 * MAX_DECOMPRESSED_SIZE guard fires *during* inflation (from `onData`),
 * aborting before the full output buffer is materialized. Using the one-shot
 * `ungzip()` would allocate the whole output first, so a malicious payload
 * (e.g. 30 MiB of repeated bytes expanding to ~30 GiB) would OOM the Worker
 * isolate before any size check could run.
 */
const MAX_COMPRESSED_SIZE = 30 * 1024 * 1024; // 30 MiB
const MAX_DECOMPRESSED_SIZE = 200 * 1024 * 1024; // 200 MiB
// Plain-JSON messages are tiny in normal operation (a card push is a few KB).
// Anything large enough to strain JSON.parse should be compressed. Capping the
// plain path prevents a single oversized frame from stalling the DO input gate
// for other connected users. The one legitimate large plain frame is the
// client's full `sync-init` timestamp manifest, which the client sends
// UNcompressed (~90 B per card-timestamp entry): a 100k-card account produces
// ~9 MB, and at 4 MiB the cap bricked those accounts in a
// sync-request-manifest → "Invalid message format" reconnect loop. 15 MiB
// covers ~170k cards; the durable fix is gz-encoding the outbound manifest
// client-side.
const MAX_PLAIN_JSON_SIZE = 15 * 1024 * 1024; // 15 MiB

export function decompressMessage(data: ArrayBuffer | Uint8Array): unknown {
  const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
  if (bytes.byteLength > MAX_COMPRESSED_SIZE) {
    throw new Error(
      `Compressed payload (${bytes.byteLength} bytes) exceeds ${MAX_COMPRESSED_SIZE} byte limit`,
    );
  }

  // windowBits 15 + 16 = gzip framing (matches pako.ungzip)
  const inflator = new Inflate({ windowBits: 15 + 16 });
  const chunks: Uint8Array[] = [];
  let total = 0;

  inflator.onData = (chunk: Uint8Array) => {
    total += chunk.length;
    if (total > MAX_DECOMPRESSED_SIZE) {
      // Throwing from onData propagates synchronously out of push() and halts
      // further inflation — no more chunks are produced after this point.
      throw new Error(`Decompressed size exceeds ${MAX_DECOMPRESSED_SIZE} byte limit`);
    }
    chunks.push(chunk);
  };

  inflator.push(bytes, true);
  if (inflator.err) {
    throw new Error(`Inflate failed: ${inflator.msg ?? 'unknown'}`);
  }

  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  const json = new TextDecoder().decode(result);
  return JSON.parse(json);
}

// ---------------------------------------------------------------------------
// String-based envelope (for y-partyserver custom messages)
//
// y-partyserver's custom-message channel is string-only (binary frames are
// reserved for the Yjs sync protocol). For large JSON payloads we gzip the
// bytes, base64-encode the result, and prefix with `gz:` so the receiver
// knows whether to decompress.
// ---------------------------------------------------------------------------

const COMPRESSED_PREFIX = 'gz:';

/**
 * Bytes ⇄ base64 codec. The default is the portable `atob`/`btoa` + per-byte
 * loop, which every runtime here has (browser, Workers, Hermes). A platform
 * with a native implementation injects it via {@link setBase64Codec}: on
 * Hermes the JS loop over a multi-MB cold-start `sync-diff` is a visible slice
 * of the WS callback, and `react-native-quick-base64` does it in C++ — the RN
 * sync bootstrap installs it. Shared code never imports the native package.
 */
export type Base64Codec = {
  toBytes: (b64: string) => Uint8Array;
  fromBytes: (bytes: Uint8Array) => string;
};

const portableBase64Codec: Base64Codec = {
  fromBytes: (bytes) => {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    return btoa(binary);
  },
  toBytes: (b64) => {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  },
};

let base64Codec: Base64Codec = portableBase64Codec;

/**
 * Install a platform base64 codec (call once at sync-client init). Pass
 * `null` to restore the portable default. Both directions must round-trip
 * with the default — the spec pins that, because a wrong codec here is
 * silent corruption of every compressed frame, not an error.
 */
export function setBase64Codec(codec: Base64Codec | null): void {
  base64Codec = codec ?? portableBase64Codec;
}

/**
 * Encode a `Uint8Array` as a base64 string.
 *
 * Used internally for the `gz:` envelope, and re-exported so callers
 * with their own binary-over-string need (e.g. inlining a Yjs update
 * into a custom-message JSON envelope) don't need to reinvent the
 * `btoa(String.fromCharCode(...))` dance — and don't need to depend on
 * Node's `Buffer` or other platform-specific bytes-to-base64 paths.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  return base64Codec.fromBytes(bytes);
}

/** Decode a base64 string back into a `Uint8Array`. */
export function base64ToBytes(b64: string): Uint8Array {
  return base64Codec.toBytes(b64);
}

/** Gzip a JSON string and wrap as a `gz:<base64>` envelope. */
export function encodeCompressedString(json: string): string {
  return COMPRESSED_PREFIX + bytesToBase64(gzip(json));
}

/**
 * Parse a custom-message payload. Handles both shapes:
 *   - `gz:<base64>` → decompress, parse JSON.
 *   - otherwise   → parse JSON directly.
 *
 * The decompression branch inherits the bomb defenses from
 * `decompressMessage` (MAX_DECOMPRESSED_SIZE guard during inflation).
 */
export function decodeCustomPayload(payload: string): unknown {
  if (payload.startsWith(COMPRESSED_PREFIX)) {
    const b64 = payload.slice(COMPRESSED_PREFIX.length);
    const bytes = base64ToBytes(b64);
    return decompressMessage(bytes);
  }
  if (payload.length > MAX_PLAIN_JSON_SIZE) {
    throw new Error(
      `Plain JSON payload (${payload.length} bytes) exceeds ${MAX_PLAIN_JSON_SIZE} byte limit — use compressed envelope`,
    );
  }
  return JSON.parse(payload);
}
